import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import type { StudioConfigData } from "@production-hub/shared";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

type StudioConfig = {
  id: string;
  studio_id: string;
  project_id: string | null;
  name: string;
  description: string | null;
  version: number;
  config_data: StudioConfigData;
  created_at: string;
  updated_at: string;
};

type StudioConfigVersion = {
  id: string;
  config_id: string;
  version: number;
  config_data: StudioConfigData;
  created_at: string;
};

type ZeroStudioConfig = {
  id: string;
  studioID: string;
  projectID?: string | null;
  name: string;
  description?: string | null;
  version: number;
  configData?: StudioConfigData;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroStudioConfigVersion = {
  id: string;
  configID: string;
  version: number;
  configData?: StudioConfigData;
  createdAt?: number | string | null;
};

const emptyConfigData: StudioConfigData = {
  layouts: [],
  profiles: [],
};

function toLegacyConfig(row: ZeroStudioConfig): StudioConfig {
  return {
    id: row.id,
    studio_id: row.studioID,
    project_id: row.projectID ?? null,
    name: row.name,
    description: row.description ?? null,
    version: row.version,
    config_data: row.configData ?? emptyConfigData,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyVersion(row: ZeroStudioConfigVersion): StudioConfigVersion {
  return {
    id: row.id,
    config_id: row.configID,
    version: row.version,
    config_data: row.configData ?? emptyConfigData,
    created_at: toLegacyTimestamp(row.createdAt),
  };
}

export const useStudioConfigs = (studioId: string | undefined) => {
  const query = studioId
    ? appQueries.studioConfigs.byStudio({ studioID: studioId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!studioId });
  const configs = ((data ?? []) as ZeroStudioConfig[]).map(toLegacyConfig);
  return {
    data: configs,
    isLoading:
      !!studioId && details.type !== "complete" && configs.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useSingleStudioConfig = (configId: string | undefined) => {
  const query = configId
    ? appQueries.studioConfigs.byID({ id: configId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!configId });
  return {
    data: data ? toLegacyConfig(data as ZeroStudioConfig) : null,
    isLoading: !!configId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateStudioConfig = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (params: {
    studio_id: string;
    name: string;
    description?: string | null;
    project_id?: string | null;
    config_data?: StudioConfigData;
  }) =>
    runPending(async () => {
      const input = {
        id: generateUUID(),
        studioID: params.studio_id,
        name: params.name,
        description: params.description ?? null,
        projectID: params.project_id ?? null,
        configData: params.config_data ?? emptyConfigData,
      };
      const mutation = zero.mutate(appMutators.studioConfigs.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio config create was rejected",
      );
      return toLegacyConfig({ ...input, version: 1 });
    });
  return {
    mutate: (
      params: {
        studio_id: string;
        name: string;
        description?: string | null;
        project_id?: string | null;
        config_data?: StudioConfigData;
      },
      options?: MutationOptions<StudioConfig>,
    ) => {
      void run(params)
        .then((data) => {
          toast.success("Configuration created");
          options?.onSuccess?.(data);
        })
        .catch((err) => toast.error("Failed to create config: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useUpdateStudioConfig = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (params: {
    id: string;
    studio_id: string;
    name?: string;
    description?: string | null;
    config_data?: StudioConfigData;
  }) =>
    runPending(async () => {
      const input = {
        id: params.id,
        studioID: params.studio_id,
        ...(params.name !== undefined ? { name: params.name } : {}),
        ...(params.description !== undefined
          ? { description: params.description }
          : {}),
        ...(params.config_data !== undefined
          ? { configData: params.config_data }
          : {}),
      };
      const mutation = zero.mutate(appMutators.studioConfigs.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio config update was rejected",
      );
      return toLegacyConfig({
        ...input,
        name: input.name ?? "",
        version: 1,
      });
    });
  return {
    mutate: (
      params: {
        id: string;
        studio_id: string;
        name?: string;
        description?: string | null;
        config_data?: StudioConfigData;
      },
      options?: MutationOptions<StudioConfig>,
    ) => {
      void run(params)
        .then((data) => options?.onSuccess?.(data))
        .catch((err) => toast.error("Failed to update config: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useDeleteStudioConfig = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (params: { id: string; studio_id: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.studioConfigs.delete({
          id: params.id,
          studioID: params.studio_id,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio config delete was rejected",
      );
    });
  return {
    mutate: (
      params: { id: string; studio_id: string },
      options?: { onSuccess?: () => void },
    ) => {
      void run(params)
        .then(() => {
          toast.success("Configuration deleted");
          options?.onSuccess?.();
        })
        .catch((err) => toast.error("Failed to delete config: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useExportBridgeConfig = () =>
  usePendingBridgeMutation(
    async (bridgeUrl: string): Promise<StudioConfigData> => {
      const response = await fetch(`${bridgeUrl}/api/config`);
      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      return response.json();
    },
  );

export const useLoadConfigToBridge = () =>
  usePendingBridgeMutation(
    async (params: { bridgeUrl: string; configData: StudioConfigData }) => {
      const response = await fetch(`${params.bridgeUrl}/api/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params.configData),
      });
      if (!response.ok) throw new Error(`Load failed: ${response.status}`);
      return response.json();
    },
    {
      success: "Configuration loaded to bridge",
      error: "Failed to load: ",
    },
  );

export const useConfigureBridgeSync = () =>
  usePendingBridgeMutation(
    async (params: {
      bridgeUrl: string;
      apiUrl: string;
      configId: string;
      apiKey: string;
    }) => {
      const response = await fetch(`${params.bridgeUrl}/api/sync/configure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_url: params.apiUrl,
          config_id: params.configId,
          api_key: params.apiKey,
        }),
      });
      if (!response.ok)
        throw new Error(`Sync configure failed: ${response.status}`);
      return response.json();
    },
  );

export const useConfigVersions = (configId: string | undefined) => {
  const query = configId
    ? appQueries.studioConfigs.versions({ configID: configId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!configId });
  const versions = ((data ?? []) as ZeroStudioConfigVersion[]).map(
    toLegacyVersion,
  );
  return {
    data: versions,
    isLoading:
      !!configId && details.type !== "complete" && versions.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useRestoreConfigVersion = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (params: {
    configId: string;
    versionId: string;
    studioId: string;
  }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.studioConfigs.restoreVersion({
          configID: params.configId,
          versionID: params.versionId,
          studioID: params.studioId,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio config restore was rejected",
      );
    });
  return {
    mutate: (
      params: { configId: string; versionId: string; studioId: string },
      options?: { onSuccess?: () => void },
    ) => {
      void run(params)
        .then(() => {
          toast.success("Version restored");
          options?.onSuccess?.();
        })
        .catch((err) => toast.error("Failed to restore: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

function usePendingBridgeMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  messages?: { success?: string; error?: string },
) {
  const { isPending, runPending } = usePendingMutation();
  const run = (input: TInput) => runPending(() => mutationFn(input));
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void run(input)
        .then((data) => {
          if (messages?.success) toast.success(messages.success);
          options?.onSuccess?.(data);
        })
        .catch((err) => toast.error((messages?.error ?? "") + err.message));
    },
    mutateAsync: run,
    isPending,
  };
}
